import { useCallback } from "react";

import { message } from "antd";
import { useLocation } from "react-router-dom";

import { routerBaseName } from "App/routes/navigation";
import { AppPagePaths } from "App/routes/paths";
import {
    AUTO_OPEN_ENTITY_TRIGGER_QUERY_PARAM,
    COPILOT_SESSION_ID_QUERY_PARAM,
    COPILOT_PROMPT_LAUNCH_ID_QUERY_PARAM,
    COPILOT_PROMPT_SOURCE_QUERY_PARAM,
    COPILOT_PROMPT_SOURCE_WIDGET,
} from "Pages/copilot/Pages/CopilotEditorPage/consts";
import { useUserInfo } from "Services/AuthService";
import { getLogger } from "Services/LoggingService";

import { createCopilotPromptLaunchId, storeCopilotWidgetPrompt } from "./copilotPromptHandoff";

const logger = getLogger("Copilot Helper");

const COPILOT_HELP_BUTTON_ACTION = "copilot_help_button_clicked";

/** Extra navigation for callers that persisted a session before opening copilot. */
export type OpenCopilotOptions = {
    sessionId?: string;
    autoOpenEntityTrigger?: boolean;
};

/**
 * Single hook for all copilot navigation.
 * Builds the URL, opens the tab, and logs the copilot_help_button_clicked action.
 * Callers are responsible for composing the full prompt string (including entity ID labels).
 */
type UseCopilotHelperResult = {
    openCopilot: (prompt: string, options?: OpenCopilotOptions) => void;
};

export function useCopilotHelper(): UseCopilotHelperResult {
    const userInfo = useUserInfo();
    const location = useLocation();

    const openCopilot = useCallback(
        (prompt: string, { sessionId, autoOpenEntityTrigger }: OpenCopilotOptions = {}) => {
            const url = new URL(routerBaseName + AppPagePaths.copilot, window.location.origin);
            const launchId = createCopilotPromptLaunchId();
            const wasPromptStored = storeCopilotWidgetPrompt(prompt, launchId);
            if (wasPromptStored) {
                url.searchParams.set(COPILOT_PROMPT_SOURCE_QUERY_PARAM, COPILOT_PROMPT_SOURCE_WIDGET);
                url.searchParams.set(COPILOT_PROMPT_LAUNCH_ID_QUERY_PARAM, launchId);
            } else {
                message.warning("Copilot opened without your prompt. Please enter it manually.");
            }
            if (sessionId) {
                url.searchParams.set(COPILOT_SESSION_ID_QUERY_PARAM, sessionId);
            }
            if (autoOpenEntityTrigger) {
                url.searchParams.set(AUTO_OPEN_ENTITY_TRIGGER_QUERY_PARAM, "true");
            }
            window.open(url.href, "_blank", "noopener,noreferrer");
            logger.action(COPILOT_HELP_BUTTON_ACTION, {
                userId: userInfo?.sub,
                userEmail: userInfo?.email,
                pageSource: location.pathname,
            });
        },
        [userInfo?.sub, userInfo?.email, location.pathname]
    );

    return { openCopilot };
}
